import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { configureStatusBar } from "../../lib/statusBar";
import {
  CheckCircle2, XCircle, Phone, MapPin, Package, Sun, Moon, Truck,
  Wallet, Package2, History as HistoryIcon, Calendar, User as UserIcon, AlertTriangle, Building2,
} from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import usePushRegistration from "../../lib/usePushRegistration";
import { personaForRole } from "../../lib/pushNotifications";
import { useRiderGpsPusher } from "../../lib/liveGps";
import { formatAddress } from "../../lib/utils.fmt";
import EmptyState, { LoadingState } from "../../components/EmptyState";
// IST calendar dates — the UTC date read as yesterday until 05:30 IST.
import { todayIST, yesterdayIST, shiftIST, tomorrowIST } from "../../lib/istDate";
import { productLine, packItemKey, filterApartmentSummaries } from "../../lib/riderLabels";

// Quick-tap reasons for failed/skipped/cancelled — keeps the mandatory-
// reason requirement fast for riders instead of forcing a bare free-text
// box. "Other" clears the field so the rider can type their own.
const REASON_PRESETS = ["Customer unavailable", "Refused", "Address issue", "Other"];

export default function RiderToday() {
  const { user, tenant, loading } = useAuth();
  const nav = useNavigate();
  // Single source of truth — when the tenant has bottle tracking turned off
  // in Admin → Settings → Operations, the rider never sees bottle UI: no
  // stat, no pickup queue, no "Bottles returned" input on the mark dialog,
  // and no "X bottles returned" history badges.
  const bottlesOn = tenant?.bottle_tracking_enabled !== false;
  // Off by default (models.Tenant.rider_cancel_enabled) — a tenant admin
  // must explicitly opt in before riders see a real "Cancel" action
  // (distinct from Fail/Skip) in Settings → Operations.
  const riderCancelOn = tenant?.rider_cancel_enabled === true;
  // 3-tab layout: today (active route) | tomorrow (pre-pack preview) | history (date picker)
  // Apartment filter. Unlike the area filter (client-side over today's rows),
  // this one is applied SERVER-SIDE and sits above the tabs, because it has
  // to narrow every screen the rider looks at — today, tomorrow, history,
  // the pack summary and the pickup list — not just one list.
  //
  // Options come from the rider's OWN deliveries (GET /api/delivery/
  // apartments), so a rider covering two blocks doesn't get a dropdown of
  // the tenant's forty. No apartments at all → the control is hidden.
  //
  // Declared HERE, with the other state, rather than beside the area filter
  // further down: the loaders and effects below close over `apartmentParam`
  // and list `apartmentFilter` in a dependency array, and a dep array is
  // evaluated during render — so declaring it later threw
  // "Cannot access 'apartmentFilter' before initialization" and took the
  // whole rider app to the error boundary on login.
  const [apartmentFilter, setApartmentFilter] = useState("__all__");
  const [apartments, setApartments] = useState([]);
  const [hasManual, setHasManual] = useState(false);
  // Apartment overview tab (GET /api/delivery/apartments/summary): which day,
  // the search box, and the loaded summary. Declared up here with the other
  // apartment state for the same dependency-array reason as above.
  const [aptDay, setAptDay] = useState("today");
  const [aptQuery, setAptQuery] = useState("");
  const [aptSummary, setAptSummary] = useState(null);
  const [aptBusy, setAptBusy] = useState(false);
  const [serverTomorrow, setServerTomorrow] = useState("");
  const showApartmentFilter = apartments.length > 0;
  // `undefined` rather than "" so the query param is omitted entirely.
  const apartmentParam = apartmentFilter === "__all__" ? undefined : apartmentFilter;

  const [tab, setTab] = useState("today");
  const [summary, setSummary] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [nextSlot, setNextSlot] = useState([]);
  const [history, setHistory] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [historyDate, setHistoryDate] = useState(yesterdayIST());
  // "single" = Today/Yesterday/date-picker (existing behavior, unchanged).
  // "range"  = new custom From -> To filter, added alongside it.
  const [historyMode, setHistoryMode] = useState("single");
  const [rangeFrom, setRangeFrom] = useState(() => shiftIST(-7));
  const [rangeTo, setRangeTo] = useState(() => todayIST());
  const [active, setActive] = useState(null);
  const [bottles, setBottles] = useState(0);
  const [notes, setNotes] = useState("");
  // Which reason chip is active — tracked separately from `notes` because
  // "Other" starts with an EMPTY notes value (the rider types their own),
  // so highlighting it via `notes === "Other"` never matched and the chip
  // looked unselectable.
  const [selectedReason, setSelectedReason] = useState(null);
  const [markType, setMarkType] = useState(null);
  const [pickupActive, setPickupActive] = useState(null);
  const [pickupActual, setPickupActual] = useState(0);
  // Pre-route packing summary — totals per product for today's open deliveries.
  const [packOpen, setPackOpen] = useState(false);
  const [packSlot, setPackSlot] = useState(null);
  const [packData, setPackData] = useState(null);
  const [packBusy, setPackBusy] = useState(false);

  const openPackSummary = async (slot = null) => {
    setPackBusy(true);
    setPackOpen(true);
    setPackSlot(slot);
    try {
      const params = new URLSearchParams();
      if (slot) params.set("slot", slot);
      if (apartmentParam) params.set("apartment_id", apartmentParam);
      const qs = params.toString() ? `?${params}` : "";
      const { data } = await api.get(`/api/delivery/today/pack-summary${qs}`);
      setPackData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pack summary failed");
      setPackData(null);
    } finally { setPackBusy(false); }
  };

  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoadError(null);
    try {
      const apt = apartmentParam ? `?apartment_id=${encodeURIComponent(apartmentParam)}` : "";
      const s = await api.get(`/api/delivery/me/summary${apt}`);
      setSummary(s.data);
      const t = await api.get(`/api/delivery/today${apt}`);
      setDeliveries(t.data.deliveries);
      setNextSlot(t.data.next_slot || []);
      setServerTomorrow(t.data.tomorrow || "");
      if (bottlesOn) {
        const p = await api.get(`/api/delivery/pickups${apt}`);
        setPickups(p.data.pickups);
      } else {
        setPickups([]);
      }
    } catch (e) {
      setLoadError(friendlyMessage(e));
    }
  };

  const loadHistory = async (params) => {
    try {
      const withApt = apartmentParam
        ? `${params}&apartment_id=${encodeURIComponent(apartmentParam)}`
        : params;
      const r = await api.get(`/api/delivery/history?${withApt}`);
      setHistory(r.data.deliveries || []);
    } catch (e) {
      toast.error(friendlyMessage(e));
    }
  };

  // Apartment overview — deliberately NOT narrowed by the apartment filter:
  // it is the list you pick an apartment from.
  const loadAptSummary = async () => {
    setAptBusy(true);
    try {
      const params = new URLSearchParams();
      if (aptDay === "tomorrow") params.set("target_date", serverTomorrow || tomorrowIST());
      const qs = params.toString() ? `?${params}` : "";
      const { data } = await api.get(`/api/delivery/apartments/summary${qs}`);
      setAptSummary(data);
    } catch (e) {
      toast.error(friendlyMessage(e));
      setAptSummary(null);
    } finally { setAptBusy(false); }
  };

  /** Tap an apartment → the existing per-customer list for that day,
   *  narrowed to it through the server-side apartment filter. */
  const openApartment = (aptId) => {
    setApartmentFilter(aptId);
    setTab(aptDay === "tomorrow" ? "tomorrow" : "today");
  };

  const applyRange = () => {
    if (!rangeFrom || !rangeTo) { toast.error("Pick both a From and To date"); return; }
    if (rangeFrom > rangeTo) { toast.error("From date must be on or before the To date"); return; }
    loadHistory(`date_from=${rangeFrom}&date_to=${rangeTo}`);
  };

  useEffect(() => { if (!loading && (!user || user.role !== "delivery_boy")) nav("/delivery/login"); }, [user, loading]);
  useEffect(() => { configureStatusBar({ bgColor: "#111827", style: "light" }); }, []);
  useEffect(() => { if (user?.role === "delivery_boy") load(); }, [user]);
  useEffect(() => {
    if (user?.role !== "delivery_boy") return;
    api.get("/api/delivery/apartments").then(({ data }) => {
      setApartments(data.apartments || []);
      setHasManual(!!data.has_manual);
    }).catch(() => setApartments([]));
  }, [user]);
  // Changing the filter re-queries — it's server-side, so today/tomorrow,
  // the summary counts and the pickup list all move together.
  const firstApartmentRun = useRef(true);
  useEffect(() => {
    if (user?.role !== "delivery_boy") return;
    if (firstApartmentRun.current) { firstApartmentRun.current = false; return; }
    load();
    if (tab === "history") {
      if (historyMode === "single") loadHistory(`date=${historyDate}`);
      else applyRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartmentFilter]);
  // Single-date mode auto-loads on every date change (unchanged behavior).
  // `loadHistory` is intentionally not a dependency: it now closes over the
  // apartment filter, so listing it would re-run this effect on every filter
  // change — which the dedicated apartment effect above already does, and
  // doing both would fire two history requests per switch.
  useEffect(() => {
    if (user?.role === "delivery_boy" && tab === "history" && historyMode === "single") loadHistory(`date=${historyDate}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab, historyMode, historyDate]);
  // Range mode loads once with the default 7-day window as soon as it's
  // opened; further From/To edits require tapping Apply (avoids firing a
  // request mid-pick, e.g. while only the From date has been changed yet).
  useEffect(() => {
    if (user?.role === "delivery_boy" && tab === "history" && historyMode === "range") applyRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab, historyMode]);
  useEffect(() => {
    if (user?.role === "delivery_boy" && tab === "apartments") loadAptSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab, aptDay]);

  // Capacitor native apps: ask for push permission + register FCM token (no-op on web)
  usePushRegistration(personaForRole(user?.role), !!user?.role);

  // Live GPS push over WebSocket every 5s while the rider is active.
  useRiderGpsPusher(user?.user_id || user?.id, {
    enabled: user?.role === "delivery_boy",
    pushIntervalMs: 5000,
    fallbackLat: summary?.me?.current_lat || 17.42,
    fallbackLng: summary?.me?.current_lng || 78.41,
  });

  // Area/route filter — a rider can be assigned multiple routes across
  // slots (a route for morning, a different one for evening, or several
  // stamped directly onto individual orders), so "today" can legitimately
  // span more than one community/society/locality. Only meaningful (and
  // only rendered, see below) when there's actually more than one distinct
  // area in today's list — a single-route rider gets no filter to fuss with.
  const [areaFilter, setAreaFilter] = useState("__all__");
  const areaOptions = useMemo(() => {
    const names = new Set(deliveries.map((d) => d.route_name || "Unassigned"));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [deliveries]);
  const areaFilteredDeliveries = useMemo(() => {
    if (areaFilter === "__all__") return deliveries;
    return deliveries.filter((d) => (d.route_name || "Unassigned") === areaFilter);
  }, [deliveries, areaFilter]);

  // Group Today's deliveries by slot (Morning / Evening / any other
  // tenant-defined slot label) — previously one flat list ordered only by
  // stop sequence, with slot never grouped or even displayed on Today cards.
  const deliveriesBySlot = useMemo(() => {
    const groups = new Map();
    // Apartment narrowing already happened server-side, so this only has to
    // apply the client-side area filter.
    areaFilteredDeliveries.forEach((d) => {
      const key = (d.slot || "").toLowerCase() || "other";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(d);
    });
    const order = ["morning", "evening"];
    const keys = Array.from(groups.keys()).sort((a, b) => {
      const ia = order.indexOf(a), ib = order.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
    return keys.map((slot) => ({ slot, items: groups.get(slot) }));
  }, [areaFilteredDeliveries]);

  // Group history by customer for the History tab
  const historyByCustomer = useMemo(() => {
    const map = new Map();
    history.forEach((d) => {
      const k = d.customer_id || "_unknown";
      if (!map.has(k)) map.set(k, {
        customer_id: k,
        customer_name: d.customer_name || "—",
        address: d.address || {},
        deliveries: [],
        delivered_count: 0,
        failed_count: 0,
        bottles_returned: 0,
      });
      const g = map.get(k);
      g.deliveries.push(d);
      if (d.status === "delivered") g.delivered_count += 1;
      if (d.status === "failed") g.failed_count += 1;
      g.bottles_returned += d.bottles_returned_count || 0;
    });
    return Array.from(map.values()).sort((a, b) => (b.delivered_count - a.delivered_count));
  }, [history]);

  if (loading) return <LoadingState variant="dark" fullHeight className="min-h-screen bg-brand-ink" />;
  if (loadError && !summary) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load your route" description={loadError}
        action={<button onClick={load} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight variant="dark" className="min-h-screen bg-brand-ink" />
    );
  }
  if (!summary) return <LoadingState variant="dark" fullHeight className="min-h-screen bg-brand-ink" />;

  /** Opens the mark dialog for a delivery — always resets notes/bottles
   * first, so a leftover reason typed for one delivery (then cancelled)
   * can never silently carry over and satisfy the required-reason check
   * for a different delivery. */
  const openMark = (delivery, type) => {
    setNotes(""); setBottles(0); setSelectedReason(null);
    setActive(delivery); setMarkType(type);
  };

  const mark = async () => {
    try {
      await api.patch(`/api/delivery/deliveries/${active.id}/mark`, {
        status: markType, bottles_returned_count: bottles, notes,
      });
      toast.success(`Marked ${markType}`);
      setActive(null); setBottles(0); setNotes(""); setSelectedReason(null); setMarkType(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark"); }
  };

  /** Directly flip status (no dialog) — used only by "Out for delivery",
   *  which needs no bottles/notes input. */
  const quickMark = async (delivery, status) => {
    try {
      await api.patch(`/api/delivery/deliveries/${delivery.id}/mark`, {
        status, bottles_returned_count: 0, notes: "",
      });
      toast.success(status === "out_for_delivery" ? "Marked out for delivery" : `Marked ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark"); }
  };

  const fulfillPickup = async () => {
    try {
      await api.patch(`/api/delivery/pickups/${pickupActive.id}/fulfill`, {
        actual_count: pickupActual,
      });
      toast.success(`Picked up ${pickupActual} bottle${pickupActual !== 1 ? 's' : ''}`);
      setPickupActive(null); setPickupActual(0);
      load();
    } catch { toast.error("Failed to mark pickup"); }
  };

  const TABS = [
    { v: "today",    label: "Today",    Icon: Sun,         count: deliveries.length },
    { v: "tomorrow", label: "Tomorrow", Icon: Calendar,    count: nextSlot.length },
    // Apartment-level view — only for riders who actually deliver into
    // serviceable apartments (same condition as the apartment filter).
    ...(showApartmentFilter
      ? [{ v: "apartments", label: "Blocks", Icon: Building2, count: null }]
      : []),
    { v: "history",  label: "History",  Icon: HistoryIcon, count: tab === "history" ? history.length : null },
  ];
  const compactTabs = TABS.length === 4;

  return (
    <div className="min-h-screen pt-safe bg-brand-ink text-white pb-12" data-testid="rider-today-page">
      <div aria-hidden className="fixed top-0 left-0 right-0 z-40 bg-brand-ink pointer-events-none"
           style={{ height: "env(safe-area-inset-top, 0px)" }}/>
      <header className="pt-safe">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wider font-bold text-white/60">Hello,</div>
            <div className="font-heading text-2xl font-extrabold tracking-tight truncate">{summary.me.name}</div>
            <div className="text-sm text-white/60 mt-1 truncate">{summary.route?.name || "No route assigned"}</div>
          </div>
          <button onClick={() => nav("/rider/profile")} className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center shrink-0" data-testid="profile-btn" aria-label="Profile">
            <UserIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Apartment filter — ABOVE the tabs on purpose: it's applied
            server-side and narrows every tab (today / tomorrow / history)
            plus the pack summary and pickups, so it must not look like it
            belongs to whichever tab happens to be open. Hidden entirely when
            this rider delivers to no apartments. */}
        {showApartmentFilter && (
          <div className="px-5 pb-3 flex items-center gap-2" data-testid="rider-apartment-filter-wrap">
            <Building2 className="w-4 h-4 text-white/70 shrink-0"/>
            <select value={apartmentFilter} onChange={(e) => setApartmentFilter(e.target.value)}
                    className="h-10 px-3 rounded-2xl bg-white/10 text-white border border-white/20 text-sm flex-1 min-w-0"
                    data-testid="rider-apartment-filter">
              <option value="__all__" className="text-brand-ink">All apartments</option>
              {apartments.map((a) => (
                <option key={a.id} value={a.id} className="text-brand-ink">{a.name}</option>
              ))}
              {/* Manual addresses, and every delivery created before the
                  apartments feature existed. */}
              {hasManual && (
                <option value="__manual__" className="text-brand-ink">Other / manual address</option>
              )}
            </select>
            {apartmentFilter !== "__all__" && (
              <button onClick={() => setApartmentFilter("__all__")}
                      className="h-10 px-3 rounded-2xl bg-white/10 border border-white/20 text-white text-xs font-bold shrink-0"
                      data-testid="rider-apartment-filter-clear">
                Clear
              </button>
            )}
          </div>
        )}

        {/* 3-tab pill */}
        <div className="px-5 pb-3" data-testid="rider-tabs">
          <div className={`grid ${compactTabs ? "grid-cols-4" : "grid-cols-3"} gap-1 p-1 rounded-2xl bg-white/10 border border-white/15`}>
            {TABS.map(({ v, label, Icon, count }) => {
              const badge = count !== null && (
                <span className={`${compactTabs ? "" : "ml-0.5 "}inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-extrabold ${tab === v ? "bg-brand-ink text-white" : "bg-white/15 text-white"}`}>
                  {count}
                </span>
              );
              return (
                <button key={v} onClick={() => setTab(v)}
                        className={`${compactTabs ? "h-12 min-w-0 flex-col gap-0.5 tracking-wide" : "h-11 gap-1 tracking-wider"} rounded-xl font-extrabold uppercase text-[11px] inline-flex items-center justify-center transition ${tab === v ? "bg-white text-brand-ink shadow" : "text-white/80"}`}
                        data-testid={`rider-tab-${v}`}>
                  {compactTabs ? (
                    // Four tabs don't fit one row at 360dp ("TOMORROW" + its
                    // count overflowed into the next tab), so the icon and
                    // count sit above the label — nothing is hidden.
                    <>
                      <span className="inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5" />{badge}</span>
                      <span className="leading-none max-[359px]:text-[10px] max-[359px]:tracking-normal">{label}</span>
                    </>
                  ) : (
                    <><Icon className="w-3.5 h-3.5" /> {label}{badge}</>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {tab === "today" && (
        <>
          {/* Stats */}
          <div className={`px-5 grid gap-3 ${bottlesOn ? "grid-cols-3" : "grid-cols-2"}`} data-testid="rider-stats">
            <Stat label="Pending" value={summary.pending} color="text-brand-sand"/>
            <Stat label="Delivered" value={summary.delivered} color="text-brand-leaf"/>
            {bottlesOn && (
              <Stat label="Bottles" value={summary.bottles_returned} color="text-brand-sky"/>
            )}
          </div>

          {/* Pre-route Pack Summary — totals per product so the rider knows what to load */}
          {deliveries.length > 0 && (
            <div className="px-5 mt-4">
              <button onClick={() => openPackSummary(null)} data-testid="rider-pack-summary-btn"
                      className="w-full h-12 rounded-2xl bg-brand-ink text-white font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 btn-tactile">
                <Package className="w-4 h-4"/> Pack Summary · {deliveries.length} deliveries
              </button>
            </div>
          )}

          {/* Bottle pickups section — only when the tenant enables bottle tracking */}
          {bottlesOn && pickups.length > 0 && (
            <div className="px-5 mt-6" data-testid="rider-pickups-section">
              <h2 className="font-heading text-xl font-extrabold tracking-tight mb-3 flex items-center gap-2">
                <Package2 className="w-5 h-5 text-brand-sand"/> Collect today ({pickups.length})
              </h2>
              <div className="space-y-3">
                {pickups.map((p) => (
                  <div key={p.id} className="bg-brand-sand/20 border border-brand-sand/50 rounded-3xl p-4 text-brand-ink" data-testid={`pickup-${p.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs uppercase tracking-wider font-bold text-brand-sand">Bottle pickup</div>
                        <div className="font-heading font-extrabold mt-0.5">{p.customer_name}</div>
                        <div className="text-sm flex items-start gap-1.5 mt-1 text-brand-ink/80">
                          <MapPin className="w-4 h-4 mt-0.5 shrink-0"/>
                          <span className="break-words">{formatAddress(p.customer_address)}</span>
                        </div>
                        <div className="text-xs mt-1 text-brand-ink/70">Holds 🍶 {p.customer_bottles_held} · Requested {p.count}</div>
                        {p.notes && <div className="text-xs mt-1 italic">&quot;{p.notes}&quot;</div>}
                      </div>
                      <div className="text-3xl font-heading font-extrabold text-brand-sand shrink-0">🍶 {p.count}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <a href={`tel:${p.customer_phone || ""}`} className="h-12 rounded-2xl bg-white border-2 border-brand-ink/20 font-bold text-sm inline-flex items-center justify-center gap-1.5" data-testid={`pickup-call-${p.id}`}>
                        <Phone className="w-4 h-4"/> Call
                      </a>
                      <button onClick={() => { setPickupActive(p); setPickupActual(p.count); }}
                              className="h-12 rounded-2xl bg-brand-sand text-white font-black uppercase tracking-wide text-sm btn-tactile"
                              style={{ boxShadow: "0 4px 0 #D6913F" }}
                              data-testid={`pickup-collect-${p.id}`}>
                        <CheckCircle2 className="w-4 h-4 inline mr-1"/> Collected
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Area/route filter — only worth showing when today's list actually
              spans more than one route/community; a single-route rider gains
              nothing from a dropdown with one option. */}
          {areaOptions.length > 1 && (
            <div className="px-5 mt-4 flex items-center gap-2" data-testid="rider-area-filter-wrap">
              <MapPin className="w-4 h-4 text-white/70 shrink-0"/>
              <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}
                      className="h-10 px-3 rounded-2xl bg-white/10 text-white border border-white/20 text-sm flex-1 min-w-0"
                      data-testid="rider-area-filter">
                <option value="__all__" className="text-brand-ink">All areas ({deliveries.length})</option>
                {areaOptions.map((name) => (
                  <option key={name} value={name} className="text-brand-ink">
                    {name} ({deliveries.filter((d) => (d.route_name || "Unassigned") === name).length})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="px-5 mt-6" data-testid="rider-today-pane">
            {areaFilteredDeliveries.length === 0 ? (
              <EmptyState icon={Package} variant="dark"
                          title={deliveries.length === 0 ? "No deliveries today" : "No deliveries in this area"}
                          description={deliveries.length === 0 ? "Enjoy the day off 🎉" : "Try a different area, or pick \"All areas\"."}
                          testId="today-empty" />
            ) : (
              <div className="space-y-6">
                {deliveriesBySlot.map(({ slot, items }) => (
                  <div key={slot} data-testid={`slot-section-${slot}`}>
                    <div className="flex items-center gap-2 mb-3 text-white/90">
                      {slot === "morning" ? <Sun className="w-4 h-4"/> : slot === "evening" ? <Moon className="w-4 h-4"/> : <Package className="w-4 h-4"/>}
                      <h3 className="font-heading text-sm font-extrabold uppercase tracking-wider capitalize">{slot} deliveries</h3>
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-extrabold bg-white/15 text-white">{items.length}</span>
                    </div>
                    <div className="space-y-3">
                      {items.map((d) => (
                        <div key={d.id} className="bg-white text-brand-ink rounded-3xl p-5" data-testid={`delivery-${d.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Stop #{d.sequence}</div>
                              <div className="font-heading text-lg font-extrabold mt-0.5">{d.customer_name}</div>
                              <div className="text-sm text-neutral-700 mt-1 flex items-start gap-1.5">
                                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                                <span className="break-words">{formatAddress(d.address)}</span>
                              </div>
                              <div className="text-sm font-semibold mt-2 inline-flex items-center gap-1.5 bg-brand-cream px-3 py-1 rounded-full">
                                <Package className="w-3.5 h-3.5"/> {productLine(d)}
                              </div>
                            </div>
                            <StatusPill status={d.status}/>
                          </div>

                          {d.status === "delivered" ? (
                            <div className="mt-4 flex items-center gap-2 text-brand-leaf font-bold text-sm">
                              <CheckCircle2 className="w-5 h-5" /> Delivered{bottlesOn && d.bottles_returned_count > 0 && ` · ${d.bottles_returned_count} bottle(s) returned`}
                            </div>
                          ) : ["failed", "skipped", "cancelled"].includes(d.status) ? (
                            <div className="mt-4 flex items-center gap-2 text-brand-clay font-bold text-sm">
                              <XCircle className="w-5 h-5" /> {d.status}
                              {d.notes && <span className="text-neutral-500 font-normal normal-case">· {d.notes}</span>}
                            </div>
                          ) : (
                            <div className="mt-4 grid grid-cols-2 gap-2">
                              <button onClick={() => openMark(d, "delivered")}
                                      className="h-12 rounded-2xl bg-brand-leaf text-white font-black uppercase tracking-wide text-xs btn-tactile"
                                      style={{ boxShadow: "0 4px 0 #65997E" }}
                                      data-testid={`mark-delivered-${d.id}`}>
                                <CheckCircle2 className="w-4 h-4 inline mr-1" /> Delivered
                              </button>
                              {d.status !== "out_for_delivery" && (
                                <button onClick={() => quickMark(d, "out_for_delivery")}
                                        className="h-12 rounded-2xl bg-brand-sky text-white font-black uppercase tracking-wide text-xs btn-tactile"
                                        data-testid={`mark-out-${d.id}`}>
                                  <Truck className="w-4 h-4 inline mr-1" /> Out
                                </button>
                              )}
                              <button onClick={() => openMark(d, "skipped")}
                                      className="h-12 rounded-2xl bg-neutral-100 text-neutral-700 border border-neutral-200 font-black uppercase tracking-wide text-xs btn-tactile hover:bg-neutral-200"
                                      data-testid={`mark-skip-${d.id}`}>
                                Skip
                              </button>
                              <button onClick={() => openMark(d, "failed")}
                                      className="h-12 rounded-2xl bg-brand-clay text-white font-black uppercase tracking-wide text-xs btn-tactile"
                                      data-testid={`mark-failed-${d.id}`}>
                                <XCircle className="w-4 h-4 inline mr-1" /> Fail
                              </button>
                              {riderCancelOn && (
                                <button onClick={() => openMark(d, "cancelled")}
                                        className="h-12 rounded-2xl bg-neutral-800 text-white font-black uppercase tracking-wide text-xs btn-tactile col-span-2"
                                        data-testid={`mark-cancel-${d.id}`}>
                                  <XCircle className="w-4 h-4 inline mr-1" /> Cancel order
                                </button>
                              )}
                            </div>
                          )}

                          <div className="mt-3 flex items-center gap-3 text-sm">
                            <a href={`tel:${d.customer_phone || ""}`} className="inline-flex items-center gap-1 text-brand-sky font-bold" data-testid={`call-${d.id}`}>
                              <Phone className="w-4 h-4" /> Contact
                            </a>
                            <a href={d.address?.lat && d.address?.lng
                                    ? `https://maps.google.com/?q=${d.address.lat},${d.address.lng}`
                                    : `https://maps.google.com/?q=${encodeURIComponent(
                                        [d.address?.line1, d.address?.line2, d.address?.city, d.address?.pincode]
                                          .filter(Boolean).join(", "))}`}
                               target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 text-brand-sky font-bold" data-testid={`map-${d.id}`}>
                              <MapPin className="w-4 h-4" /> Directions
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {tab === "tomorrow" && (
        <div className="px-5" data-testid="rider-tomorrow-pane">
          <div className="text-xs uppercase tracking-wider font-bold text-white/80 mb-3 flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5"/> Tomorrow · packing preview
          </div>
          {nextSlot.length === 0 ? (
            <EmptyState icon={Calendar} variant="dark" title="No deliveries scheduled" description="Nothing lined up for tomorrow yet." testId="tomorrow-empty" />
          ) : (
            <div className="space-y-2">
              {nextSlot.map((d) => (
                <div key={d.id} className="bg-white/10 border border-white/15 text-white rounded-2xl p-4" data-testid={`tomorrow-card-${d.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center text-xs font-extrabold uppercase tracking-wider">
                      {String(d.slot || "").slice(0, 1) || "—"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-bold truncate flex-1">{d.customer_name || "—"}</div>
                        <StatusPill status={d.status}/>
                      </div>
                      {/* Wraps rather than truncates: the pack size and quantity sit
                          at the END of this line, so an ellipsis cut exactly them. */}
                      <div className="text-[11px] text-white/70 break-words">
                        {d.delivery_date} · {d.slot} · <span className="font-bold text-white">{productLine(d)}</span>
                      </div>
                      {formatAddress(d.address) && (
                        <div className="text-[11px] text-white/60 mt-0.5 break-words">
                          📍 {formatAddress(d.address)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact + Directions — rider-only quick actions.
                      Match the Today card so tomorrow's packing prep
                      can pre-emptively call / navigate if needed. */}
                  <div className="mt-2 flex items-center gap-3 text-sm">
                    {d.customer_phone && (
                      <a href={`tel:${d.customer_phone}`}
                         className="inline-flex items-center gap-1 text-white font-bold"
                         data-testid={`tomorrow-call-${d.id}`}>
                        <Phone className="w-4 h-4" /> Contact
                      </a>
                    )}
                    {(d.address?.lat || d.address?.line1) && (
                      <a href={d.address?.lat && d.address?.lng
                              ? `https://maps.google.com/?q=${d.address.lat},${d.address.lng}`
                              : `https://maps.google.com/?q=${encodeURIComponent(
                                  [d.address?.line1, d.address?.line2, d.address?.city, d.address?.pincode]
                                    .filter(Boolean).join(", "))}`}
                         target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-white font-bold"
                         data-testid={`tomorrow-map-${d.id}`}>
                        <MapPin className="w-4 h-4" /> Directions
                      </a>
                    )}
                  </div>

                  {/* Same 4 actions as admin panel — riders can mark tomorrow
                      deliveries in advance if a customer confirms early or
                      cancels. Terminal statuses show a summary label instead. */}
                  {d.status === "delivered" ? (
                    <div className="mt-3 flex items-center gap-2 text-brand-leaf font-bold text-xs">
                      <CheckCircle2 className="w-4 h-4" /> Delivered
                    </div>
                  ) : ["failed", "skipped", "cancelled"].includes(d.status) ? (
                    <div className="mt-3 flex items-center gap-2 text-brand-clay font-bold text-xs">
                      <XCircle className="w-4 h-4" /> {d.status}
                    </div>
                  ) : (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button onClick={() => openMark(d, "delivered")}
                              className="h-10 rounded-2xl bg-brand-leaf text-white font-black uppercase tracking-wide text-[11px] btn-tactile"
                              data-testid={`tomorrow-delivered-${d.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 inline mr-1"/> Delivered
                      </button>
                      {d.status !== "out_for_delivery" && (
                        <button onClick={() => quickMark(d, "out_for_delivery")}
                                className="h-10 rounded-2xl bg-brand-sky text-white font-black uppercase tracking-wide text-[11px] btn-tactile"
                                data-testid={`tomorrow-out-${d.id}`}>
                          <Truck className="w-3.5 h-3.5 inline mr-1"/> Out
                        </button>
                      )}
                      <button onClick={() => openMark(d, "skipped")}
                              className="h-10 rounded-2xl bg-white/10 text-white border border-white/20 font-black uppercase tracking-wide text-[11px] btn-tactile"
                              data-testid={`tomorrow-skip-${d.id}`}>
                        Skip
                      </button>
                      <button onClick={() => openMark(d, "failed")}
                              className="h-10 rounded-2xl bg-brand-clay text-white font-black uppercase tracking-wide text-[11px] btn-tactile"
                              data-testid={`tomorrow-fail-${d.id}`}>
                        <XCircle className="w-3.5 h-3.5 inline mr-1"/> Fail
                      </button>
                      {riderCancelOn && (
                        <button onClick={() => openMark(d, "cancelled")}
                                className="h-10 rounded-2xl bg-neutral-900 text-white border border-white/20 font-black uppercase tracking-wide text-[11px] btn-tactile col-span-2"
                                data-testid={`tomorrow-cancel-${d.id}`}>
                          <XCircle className="w-3.5 h-3.5 inline mr-1"/> Cancel order
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "apartments" && (
        <div className="px-5" data-testid="rider-apartments-pane">
          <div className="flex items-center gap-2 mb-3">
            <div className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-white/10 border border-white/15 shrink-0">
              {[["today", "Today"], ["tomorrow", "Tomorrow"]].map(([v, label]) => (
                <button key={v} onClick={() => setAptDay(v)}
                        className={`h-9 px-3 rounded-xl text-[11px] font-extrabold uppercase tracking-wider ${aptDay === v ? "bg-white text-brand-ink" : "text-white/80"}`}
                        data-testid={`rider-apt-day-${v}`}>{label}</button>
              ))}
            </div>
            <Input value={aptQuery} onChange={(e) => setAptQuery(e.target.value)} placeholder="Search apartment"
                   className="h-11 rounded-2xl bg-white/10 border-white/20 text-white placeholder:text-white/50 flex-1 min-w-0"
                   data-testid="rider-apt-search"/>
          </div>
          {aptBusy && !aptSummary ? (
            <LoadingState variant="dark" />
          ) : !aptSummary || filterApartmentSummaries(aptSummary.apartments, aptQuery).length === 0 ? (
            <EmptyState icon={Building2} variant="dark"
                        title={aptQuery ? "No matching apartment" : "Nothing to deliver"}
                        description={aptQuery ? "Try another name." : `No open deliveries ${aptDay === "tomorrow" ? "tomorrow" : "today"}.`}
                        testId="rider-apt-empty" />
          ) : (
            <div className="space-y-3">
              {filterApartmentSummaries(aptSummary.apartments, aptQuery).map((a) => (
                <button key={a.apartment_id} onClick={() => openApartment(a.apartment_id)}
                        className="w-full text-left bg-white text-brand-ink rounded-3xl p-4"
                        data-testid={`rider-apt-card-${a.apartment_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-heading text-lg font-extrabold truncate flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 shrink-0"/> {a.apartment_name}
                      </div>
                      {a.address && <div className="text-xs text-neutral-500 truncate">{a.address}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-heading text-2xl font-extrabold leading-none">{a.open_deliveries}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">to deliver</div>
                    </div>
                  </div>
                  {a.items.length > 0 && (
                    <ul className="mt-3 space-y-1" data-testid={`rider-apt-items-${a.apartment_id}`}>
                      {a.items.map((it) => (
                        <li key={packItemKey(it)} className="flex items-center justify-between gap-2 text-sm">
                          <span className="min-w-0 break-words">
                            {it.product_name}{it.pack_label ? <> — <span className="whitespace-nowrap">{it.pack_label}</span></> : ""}
                          </span>
                          <span className="font-extrabold shrink-0">{it.total_quantity}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 text-[11px] text-neutral-500">
                    {a.customer_count} {a.customer_count === 1 ? "customer" : "customers"}
                    {a.done?.delivered ? ` · ${a.done.delivered} delivered` : ""}
                    {a.done?.failed ? ` · ${a.done.failed} failed` : ""}
                    {a.done?.skipped ? ` · ${a.done.skipped} skipped` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "history" && (
        <div className="px-5">
          <div className="flex items-center gap-2 mb-3" data-testid="history-mode-tabs">
            <button onClick={() => setHistoryMode("single")}
                    className={`h-9 px-4 rounded-full text-xs font-bold ${historyMode === "single" ? "bg-white text-brand-ink" : "bg-white/10 text-white/70"}`}
                    data-testid="history-mode-single">
              Single Date
            </button>
            <button onClick={() => setHistoryMode("range")}
                    className={`h-9 px-4 rounded-full text-xs font-bold ${historyMode === "range" ? "bg-white text-brand-ink" : "bg-white/10 text-white/70"}`}
                    data-testid="history-mode-range">
              Custom Range
            </button>
          </div>

          {historyMode === "single" ? (
            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="history-date-picker-wrap">
              <Calendar className="w-4 h-4 text-white/70"/>
              <span className="text-sm text-white/70">Show</span>
              <input type="date" value={historyDate} onChange={(e) => setHistoryDate(e.target.value)}
                     className="h-10 px-3 rounded-2xl bg-white/10 text-white border border-white/20 text-sm"
                     data-testid="history-date-picker"/>
              <button onClick={() => setHistoryDate(yesterdayIST())}
                      className="h-10 px-3 rounded-2xl bg-white/10 text-white/80 text-xs font-bold"
                      data-testid="history-yesterday">
                Yesterday
              </button>
              <button onClick={() => setHistoryDate(todayIST())}
                      className="h-10 px-3 rounded-2xl bg-white/10 text-white/80 text-xs font-bold"
                      data-testid="history-today-btn">
                Today
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="history-range-picker-wrap">
              <Calendar className="w-4 h-4 text-white/70"/>
              <span className="text-sm text-white/70">From</span>
              <input type="date" value={rangeFrom} max={rangeTo} onChange={(e) => setRangeFrom(e.target.value)}
                     className="h-10 px-3 rounded-2xl bg-white/10 text-white border border-white/20 text-sm"
                     data-testid="history-range-from"/>
              <span className="text-sm text-white/70">To</span>
              <input type="date" value={rangeTo} min={rangeFrom} max={todayIST()} onChange={(e) => setRangeTo(e.target.value)}
                     className="h-10 px-3 rounded-2xl bg-white/10 text-white border border-white/20 text-sm"
                     data-testid="history-range-to"/>
              <button onClick={applyRange}
                      className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-xs font-bold"
                      data-testid="history-range-apply">
                Apply
              </button>
            </div>
          )}

          <div className="text-sm text-white/70 mb-3" data-testid="history-summary">
            {history.length} deliveries · {historyByCustomer.length} customers ·{" "}
            {history.filter((d) => d.status === "delivered").length} delivered
          </div>

          <div className="space-y-3" data-testid="history-customer-list">
            {historyByCustomer.map((g) => (
              <div key={g.customer_id} className="bg-white text-brand-ink rounded-3xl p-5" data-testid={`history-customer-${g.customer_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg font-extrabold">{g.customer_name}</div>
                    <div className="text-sm text-neutral-600 flex items-start gap-1.5 mt-1">
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0"/>
                      <span className="break-words">{formatAddress(g.address)}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-neutral-500 uppercase tracking-wider font-bold">Stops</div>
                    <div className="font-heading text-2xl font-extrabold text-brand-leaf">{g.delivered_count}</div>
                  </div>
                </div>

                <div className="mt-3 space-y-1.5">
                  {g.deliveries.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 text-sm" data-testid={`history-line-${d.id}`}>
                      <Package className="w-3.5 h-3.5 text-neutral-400"/>
                      <span className="flex-1 min-w-0 break-words">{productLine(d)}</span>
                      <span className="capitalize text-xs text-neutral-500">{d.slot}</span>
                      <StatusPill status={d.status}/>
                    </div>
                  ))}
                </div>

                {bottlesOn && g.bottles_returned > 0 && (
                  <div className="mt-3 text-xs text-brand-sky font-bold">🍶 {g.bottles_returned} bottle{g.bottles_returned > 1 ? "s" : ""} returned</div>
                )}
              </div>
            ))}
            {historyByCustomer.length === 0 && (
              <EmptyState icon={HistoryIcon} variant="dark" title="No deliveries"
                          description={historyMode === "range" ? `Nothing recorded from ${rangeFrom} to ${rangeTo}.` : `Nothing recorded on ${historyDate}.`}
                          testId="history-empty" />
            )}
          </div>
        </div>
      )}

      <div className="h-4" />

      {/* Mark dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="mark-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl capitalize">{markType === "delivered" ? "Mark Delivered" : `Mark ${markType}`}</DialogTitle>
          </DialogHeader>
          {active && (
            <>
              <div className="bg-brand-cream rounded-2xl p-4">
                <div className="font-bold">{active.customer_name}</div>
                <div className="text-sm text-neutral-600">{productLine(active)}</div>
              </div>
              {markType === "delivered" && bottlesOn && (
                <div className="mt-3">
                  <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Bottles Returned</label>
                  <Input type="number" value={bottles} onChange={(e) => setBottles(parseInt(e.target.value) || 0)}
                         className="h-14 rounded-2xl text-lg mt-2" data-testid="bottles-input" />
                </div>
              )}
              {markType !== "delivered" && (
                <div className="mt-3">
                  <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Reason (required)</label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="reason-chips">
                    {REASON_PRESETS.map((r) => (
                      <button key={r} type="button"
                              onClick={() => { setSelectedReason(r); setNotes(r === "Other" ? "" : r); }}
                              className={`h-9 px-3 rounded-full text-xs font-bold border ${
                                selectedReason === r ? "bg-brand-ink text-white border-brand-ink" : "bg-neutral-50 text-neutral-700 border-neutral-200"
                              }`}
                              data-testid={`reason-chip-${r.toLowerCase().replace(/\s+/g, "-")}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3">
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">
                  {markType === "delivered" ? "Notes" : selectedReason === "Other" ? "Custom reason (required)" : "Details"}
                </label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                       placeholder={markType === "delivered" ? "Optional notes" : selectedReason === "Other" ? "Type your reason…" : "Add details…"}
                       autoFocus={selectedReason === "Other"}
                       className="h-12 rounded-2xl mt-2" data-testid="notes-input" />
              </div>
              <Button onClick={mark}
                      disabled={markType !== "delivered" && !notes.trim()}
                      className={`w-full h-14 rounded-3xl font-black uppercase tracking-wide mt-3 disabled:opacity-40 ${
                        markType === "delivered" ? "bg-brand-leaf hover:bg-green-700" : "bg-brand-clay hover:bg-orange-700"
                      }`}
                      data-testid="confirm-mark-btn">
                Confirm {markType}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Pickup fulfill dialog */}
      <Dialog open={!!pickupActive} onOpenChange={(o) => !o && setPickupActive(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="pickup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Bottle pickup</DialogTitle>
          </DialogHeader>
          {pickupActive && (
            <>
              <div className="bg-brand-cream rounded-2xl p-4">
                <div className="font-bold">{pickupActive.customer_name}</div>
                <div className="text-sm text-neutral-600">Requested: 🍶 {pickupActive.count} · Holds: 🍶 {pickupActive.customer_bottles_held}</div>
              </div>
              <div className="flex items-center justify-between gap-3 mt-2">
                <span className="text-sm">How many bottles did you collect?</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPickupActual(Math.max(0, pickupActual - 1))} className="w-10 h-10 rounded-full bg-brand-cream border border-neutral-200">−</button>
                  <span className="font-mono font-extrabold text-2xl w-12 text-center" data-testid="pickup-count">{pickupActual}</span>
                  <button onClick={() => setPickupActual(pickupActual + 1)} className="w-10 h-10 rounded-full bg-brand-cream border border-neutral-200">+</button>
                </div>
              </div>
              <Button onClick={fulfillPickup}
                      className="h-14 rounded-3xl bg-brand-sand text-white hover:bg-orange-600 font-black uppercase tracking-wide mt-3"
                      data-testid="confirm-pickup-btn">
                Confirm {pickupActual} collected
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Pack Summary dialog — pre-route per-product totals */}
      <Dialog open={packOpen} onOpenChange={(o) => { if (!o) { setPackOpen(false); setPackData(null); } }}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="pack-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Pack for today</DialogTitle>
          </DialogHeader>
          {packBusy && <div className="text-neutral-500 text-sm py-6 text-center">Calculating…</div>}
          {!packBusy && packData && (
            <>
              {/* Slot filter chips */}
              <div className="flex gap-2 flex-wrap" data-testid="pack-slot-chips">
                {[
                  { v: null, label: "All slots" },
                  { v: "morning", label: "Morning" },
                  { v: "evening", label: "Evening" },
                ].map((s) => (
                  <button key={s.label} onClick={() => openPackSummary(s.v)}
                          data-testid={`pack-slot-${s.v || "all"}`}
                          className={`h-9 px-3 rounded-full text-xs font-bold border ${
                            packSlot === s.v
                              ? "bg-brand-ink text-white border-brand-ink"
                              : "bg-white text-neutral-700 border-neutral-200"
                          }`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="bg-brand-cream rounded-2xl p-3 mt-3 text-sm" data-testid="pack-summary-totals">
                <div className="flex justify-between"><span className="text-neutral-600">Deliveries</span><span className="font-bold">{packData.total_deliveries}</span></div>
                <div className="flex justify-between"><span className="text-neutral-600">Customers</span><span className="font-bold">{packData.total_customers}</span></div>
                <div className="flex justify-between"><span className="text-neutral-600">Slot</span><span className="font-bold capitalize">{packData.slot}</span></div>
              </div>
              {packData.totals_by_unit && packData.totals_by_unit.length > 0 && (
                <div className="bg-white border border-neutral-200 rounded-2xl p-3 mt-2 flex flex-wrap gap-2"
                     data-testid="pack-summary-totals-by-unit">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold w-full">Total volume to load</div>
                  {packData.totals_by_unit.map((t) => (
                    <span key={t.unit}
                          data-testid={`pack-total-unit-${t.unit}`}
                          className="px-3 py-1 rounded-full bg-brand-ink text-white text-xs font-bold">
                      {t.label}
                    </span>
                  ))}
                </div>
              )}
              {packData.items.length === 0 ? (
                <EmptyState icon={Package2} size="sm" title="Nothing to pack"
                            description="No open deliveries for this slot." testId="pack-summary-empty" />
              ) : (
                <ul className="space-y-2 mt-3 max-h-[55vh] overflow-y-auto" data-testid="pack-summary-list">
                  {packData.items.map((it) => (
                    <li key={packItemKey(it)} className="bg-white border border-neutral-200 rounded-2xl p-3 flex items-center gap-3"
                        data-testid={`pack-item-${packItemKey(it)}`}>
                      <div className="w-12 h-12 rounded-xl bg-brand-cream overflow-hidden shrink-0">
                        {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover"/> : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{it.product_name}</div>
                        <div className="text-[11px] text-neutral-500">{it.pack_label || it.pack_size}{(it.pack_label || it.pack_size) ? " · " : ""}{it.customer_count} {it.customer_count === 1 ? "customer" : "customers"}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-heading text-2xl font-extrabold leading-none">{it.total_quantity}</div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">to pack</div>
                        {it.volume_label && it.volume_unit !== "pack" && (
                          <div className="text-[11px] mt-0.5 font-bold text-brand-ink"
                               data-testid={`pack-item-volume-${packItemKey(it)}`}>
                            = {it.volume_label}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-2xl p-4 text-center">
      <div className={`font-heading text-3xl font-extrabold ${color}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider font-bold text-white/60 mt-1">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { c: "bg-neutral-100 text-neutral-700", l: "Pending" },
    out_for_delivery: { c: "bg-brand-sky/10 text-brand-sky", l: "Active" },
    delivered: { c: "bg-brand-leaf/10 text-brand-leaf", l: "Done" },
    failed: { c: "bg-brand-clay/10 text-brand-clay", l: "Failed" },
    skipped: { c: "bg-neutral-100 text-neutral-500", l: "Skipped" },
  };
  const x = map[status] || map.pending;
  return <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${x.c}`}>{x.l}</span>;
}
